import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ChevronLeft, ChevronRight, ArrowRight, Star, BookOpen, Sparkles, Quote } from "lucide-react";
import * as Icons from "lucide-react";

export default function Home() {
  const { settings, isMenuHidden } = useSettings();
  const [banners, setBanners] = useState([]);
  const [books, setBooks] = useState([]);
  const [services, setServices] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [posts, setPosts] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);

  useEffect(() => {
    api.get("/banners?status=published").then((r) => setBanners(r.data));
    api.get("/books?status=published,coming_soon").then((r) => setBooks(r.data));
    api.get("/services?status=published").then((r) => setServices(r.data));
    api.get("/authors?status=published").then((r) => setAuthors(r.data));
    api.get("/events?status=published").then((r) => setEvents(r.data));
    api.get("/testimonials?status=published").then((r) => setTestimonials(r.data));
    api.get("/partners?status=published").then((r) => setPartners(r.data));
    api.get("/blog?status=published").then((r) => setPosts(r.data));
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setBannerIdx((i) => (i + 1) % banners.length), 6500);
    return () => clearInterval(t);
  }, [banners.length]);

  const featuredBooks = books.filter((b) => b.featured).slice(0, 6);
  const featuredAuthors = authors.filter((a) => a.featured).slice(0, 3);
  const featuredServices = services.filter((s) => s.featured).slice(0, 4);
  const currentBanner = banners[bannerIdx];

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[85vh] bg-[color:var(--marupiara-obsidian)] overflow-hidden">
        {currentBanner && (
          <div className="absolute inset-0">
            <img
              key={currentBanner.id}
              src={currentBanner.image_url}
              alt={currentBanner.title}
              className="w-full h-full object-cover fade-up"
              style={{ opacity: 0.55 }}
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.55), rgba(10,10,10,0.85))" }} />
          </div>
        )}
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32 lg:py-40">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 overline mb-6" style={{ color: currentBanner?.text_color || "var(--marupiara-gold-light)" }}>
              <Sparkles size={14} /> Marupiara Editoração
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-8 tracking-tight" style={{ color: currentBanner?.text_color || "var(--marupiara-ivory)" }}>
              {currentBanner?.title || "Onde histórias ganham vida"}
            </h1>
            <p className="text-lg sm:text-xl max-w-2xl mb-10 leading-relaxed" style={{ color: currentBanner?.text_color ? currentBanner.text_color : "rgba(253, 251, 247, 0.85)", opacity: currentBanner?.text_color ? 0.9 : 1 }}>
              {currentBanner?.subtitle || "Editora dedicada a transformar manuscritos em obras memoráveis. Publicação, diagramação, revisão, ISBN e muito mais."}
            </p>
            <div className="flex flex-wrap gap-4">
              {currentBanner?.cta_link && !isMenuHidden(currentBanner.cta_link) ? (
                <Link data-testid="hero-cta-primary" to={currentBanner.cta_link} className="btn-gold text-sm">
                  {currentBanner.cta_label || "Saber mais"} <ArrowRight size={16} />
                </Link>
              ) : !isMenuHidden("/contato") ? (
                <Link data-testid="hero-cta-primary" to="/contato" className="btn-gold text-sm">
                  Solicitar Orçamento <ArrowRight size={16} />
                </Link>
              ) : null}
              {!isMenuHidden("/livraria") && (
                <Link data-testid="hero-cta-secondary" to="/livraria" className="btn-outline-gold text-sm">
                  <BookOpen size={16} /> Explorar Livraria
                </Link>
              )}
            </div>
          </div>

          {banners.length > 1 && (
            <div className="absolute bottom-10 left-6 lg:left-10 flex items-center gap-3">
              <button data-testid="banner-prev" onClick={() => setBannerIdx((i) => (i - 1 + banners.length) % banners.length)} className="p-2 border border-[color:var(--marupiara-gold)]/40 text-[color:var(--marupiara-gold)] hover:bg-[color:var(--marupiara-gold)]/10 rounded-full">
                <ChevronLeft size={18} />
              </button>
              <div className="flex gap-2">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`banner-dot-${i}`}
                    onClick={() => setBannerIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === bannerIdx ? "w-10 bg-[color:var(--marupiara-gold)]" : "w-4 bg-white/30"}`}
                  />
                ))}
              </div>
              <button data-testid="banner-next" onClick={() => setBannerIdx((i) => (i + 1) % banners.length)} className="p-2 border border-[color:var(--marupiara-gold)]/40 text-[color:var(--marupiara-gold)] hover:bg-[color:var(--marupiara-gold)]/10 rounded-full">
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* INSTITUCIONAL */}
      {!isMenuHidden("/quem-somos") && (
      <section className="py-24 bg-[color:var(--marupiara-ivory)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="overline text-[color:var(--marupiara-gold-dark)] mb-4">Sobre a Editora</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.1] mb-6 text-[color:var(--marupiara-obsidian)]">
              Palavras que atravessam o tempo, edições que atravessam gerações.
            </h2>
            <p className="text-neutral-700 text-lg leading-relaxed mb-6">
              A Marupiara Editoração nasceu do encontro entre paixão pela literatura e compromisso com a excelência editorial. Cuidamos de cada obra como uma peça única — do manuscrito à estante do leitor.
            </p>
            <p className="text-[color:var(--marupiara-text-on-light-muted)] leading-relaxed mb-8">
              Somos referência em publicação de livros, diagramação, revisão, criação de capas, ISBN e assessoria editorial para autores estreantes e experientes.
            </p>
            <Link data-testid="home-about-cta" to="/quem-somos" className="btn-outline-gold text-sm border-[color:var(--marupiara-gold-dark)] text-[color:var(--marupiara-gold-dark)]">
              Nossa História <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <img src={settings.home_about_image_1 || "https://images.unsplash.com/photo-1532012197267-da84d127e765?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"} alt="Livros abertos" className="w-full h-64 lg:h-80 object-cover rounded-sm" />
            <img src={settings.home_about_image_2 || "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"} alt="Pilha de livros" className="w-full h-64 lg:h-80 object-cover rounded-sm mt-10" />
          </div>
        </div>
      </section>
      )}

      {/* SERVIÇOS */}
      {!isMenuHidden("/servicos") && featuredServices.length > 0 && (
        <section className="py-24 bg-[color:var(--marupiara-obsidian)] text-white relative">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
              <div>
                <div className="overline text-[color:var(--marupiara-gold)] mb-3">Nossos Serviços</div>
                <h2 className="font-serif text-4xl sm:text-5xl max-w-2xl">Cuidado editorial em todas as etapas.</h2>
              </div>
              <Link data-testid="home-services-all" to="/servicos" className="text-sm text-[color:var(--marupiara-gold)] hover:text-[color:var(--marupiara-gold-light)] flex items-center gap-2">
                Ver todos os serviços <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredServices.map((s, idx) => {
                const Icon = Icons[s.icon] || Icons.BookOpen;
                return (
                  <div
                    key={s.id}
                    data-testid={`home-service-${s.id}`}
                    className="p-8 border border-[color:var(--marupiara-gold)]/25 hover:border-[color:var(--marupiara-gold)] transition-colors bg-[color:var(--marupiara-charcoal)] group"
                  >
                    <div className="mb-6 h-12 w-12 flex items-center justify-center border border-[color:var(--marupiara-gold)]/30 group-hover:bg-[color:var(--marupiara-gold)]/10 transition-colors">
                      <Icon size={22} className="text-[color:var(--marupiara-gold)]" />
                    </div>
                    <h3 className="font-serif text-2xl mb-3 text-white">{s.name}</h3>
                    <p className="text-sm text-white/70 leading-relaxed">{s.short_description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* LIVROS DESTAQUE */}
      {!isMenuHidden("/livraria") && featuredBooks.length > 0 && (
        <section className="py-24 bg-[color:var(--marupiara-cream)]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
              <div>
                <div className="overline text-[color:var(--marupiara-gold-dark)] mb-3">Livros em Destaque</div>
                <h2 className="font-serif text-4xl sm:text-5xl text-[color:var(--marupiara-obsidian)] max-w-2xl">Obras que celebram nossa cultura literária.</h2>
              </div>
              <Link data-testid="home-books-all" to="/livraria" className="text-sm text-[color:var(--marupiara-gold-dark)] hover:text-[color:var(--marupiara-obsidian)] flex items-center gap-2">
                Ir para a Livraria <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {featuredBooks.map((b) => (
                <Link
                  key={b.id}
                  to={`/livro/${b.slug}`}
                  data-testid={`home-book-${b.id}`}
                  className="book-card block"
                >
                  <div className="book-card-inner">
                    <div className="relative aspect-[2/3] overflow-hidden bg-neutral-200 mb-3">
                      <img src={b.cover_url} alt={b.title} className="book-cover w-full h-full object-cover" />
                      {b.status === "coming_soon" && (
                        <span className="absolute top-2 left-2 bg-[color:var(--marupiara-obsidian)] text-[color:var(--marupiara-gold)] text-[10px] uppercase tracking-wider px-2 py-0.5 flex items-center gap-1 border border-[color:var(--marupiara-gold)]/40">
                          <Sparkles size={10} /> Em Breve
                        </span>
                      )}
                    </div>
                    <div className="overline text-[10px] text-[color:var(--marupiara-gold-dark)]">{b.category}</div>
                    <div className="font-serif text-lg text-[color:var(--marupiara-obsidian)] leading-tight line-clamp-2">{b.title}</div>
                    <div className="text-xs text-[color:var(--marupiara-text-on-light-muted)] mt-0.5">{b.author_name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AUTORES */}
      {!isMenuHidden("/autores") && featuredAuthors.length > 0 && (
        <section className="py-24 bg-[color:var(--marupiara-ivory)]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
              <div>
                <div className="overline text-[color:var(--marupiara-gold-dark)] mb-3">Autores em destaque</div>
                <h2 className="font-serif text-4xl sm:text-5xl text-[color:var(--marupiara-obsidian)] max-w-2xl">Vozes que traduzem o Brasil literário.</h2>
              </div>
              <Link data-testid="home-authors-all" to="/autores" className="text-sm text-[color:var(--marupiara-gold-dark)] hover:text-[color:var(--marupiara-obsidian)] flex items-center gap-2">
                Todos os autores <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {featuredAuthors.map((a) => (
                <Link
                  key={a.id}
                  to={`/autor/${a.slug}`}
                  data-testid={`home-author-${a.id}`}
                  className="group"
                >
                  <div className="aspect-[4/5] overflow-hidden mb-4 bg-neutral-200">
                    <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h3 className="font-serif text-2xl text-[color:var(--marupiara-obsidian)] group-hover:text-[color:var(--marupiara-gold-dark)] transition-colors">{a.name}</h3>
                  <p className="text-sm text-[color:var(--marupiara-text-on-light-muted)] mt-2 line-clamp-3">{a.biography}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EVENTOS + BLOG */}
      {(!isMenuHidden("/eventos") || !isMenuHidden("/blog")) && (
      <section className="py-24 bg-[color:var(--marupiara-obsidian)] text-white">
        <div className={`max-w-[1400px] mx-auto px-6 lg:px-10 grid gap-12 ${!isMenuHidden("/eventos") && !isMenuHidden("/blog") ? "lg:grid-cols-2" : "lg:grid-cols-1 max-w-3xl"}`}>
          {!isMenuHidden("/eventos") && (
          <div>
            <div className="overline text-[color:var(--marupiara-gold)] mb-3">Próximos Eventos</div>
            <h2 className="font-serif text-4xl mb-8">Encontre-nos por aí.</h2>
            <div className="space-y-4">
              {events.slice(0, 3).map((e) => {
                const d = new Date(e.start_date || e.date);
                const valid = !isNaN(d.getTime());
                return (
                <div key={e.id} data-testid={`home-event-${e.id}`} className="flex gap-4 p-4 border border-[color:var(--marupiara-gold)]/20 hover:border-[color:var(--marupiara-gold)]/50 transition-colors">
                  <div className="shrink-0 w-16 text-center">
                    <div className="font-serif text-3xl text-[color:var(--marupiara-gold)]">{valid ? d.getDate() : "—"}</div>
                    <div className="overline text-[10px] text-white/60">{valid ? d.toLocaleString("pt-BR", { month: "short" }) : ""}</div>
                  </div>
                  <div>
                    <h4 className="font-serif text-xl">{e.name}</h4>
                    <p className="text-xs text-white/60 mt-1">{e.location} — {e.time}</p>
                  </div>
                </div>
                );
              })}
              {events.length === 0 && <p className="text-white/70 text-sm">Nenhum evento programado no momento.</p>}
            </div>
            <Link to="/eventos" className="mt-6 inline-flex text-sm text-[color:var(--marupiara-gold)] gap-2 items-center">Todos os eventos <ArrowRight size={14} /></Link>
          </div>
          )}
          {!isMenuHidden("/blog") && (
          <div>
            <div className="overline text-[color:var(--marupiara-gold)] mb-3">Últimas do Blog</div>
            <h2 className="font-serif text-4xl mb-8">Conteúdo editorial.</h2>
            <div className="space-y-4">
              {posts.slice(0, 3).map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`home-post-${p.id}`} className="flex gap-4 p-4 border border-[color:var(--marupiara-gold)]/20 hover:border-[color:var(--marupiara-gold)]/50 transition-colors group">
                  <img src={p.cover_url} alt={p.title} className="w-24 h-24 object-cover shrink-0" />
                  <div>
                    <div className="overline text-[10px] text-[color:var(--marupiara-gold)]">{p.category}</div>
                    <h4 className="font-serif text-lg text-white group-hover:text-[color:var(--marupiara-gold-light)] transition-colors line-clamp-2">{p.title}</h4>
                    <p className="text-xs text-white/60 mt-1 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Link to="/blog" className="mt-6 inline-flex text-sm text-[color:var(--marupiara-gold)] gap-2 items-center">Ver blog completo <ArrowRight size={14} /></Link>
          </div>
          )}
        </div>
      </section>
      )}

      {/* DEPOIMENTOS */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-[color:var(--marupiara-ivory)]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="overline text-[color:var(--marupiara-gold-dark)] mb-3">Depoimentos</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[color:var(--marupiara-obsidian)]">Autores que confiam em nós.</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} data-testid={`home-testimonial-${t.id}`} className="p-8 bg-white border border-[color:var(--marupiara-gold)]/20 relative">
                  <Quote size={32} className="text-[color:var(--marupiara-gold)]/50 mb-4" />
                  <p className="text-neutral-700 leading-relaxed italic mb-6">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    {t.photo_url && <img src={t.photo_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />}
                    <div>
                      <div className="font-serif text-lg text-[color:var(--marupiara-obsidian)]">{t.name}</div>
                      <div className="text-xs text-[color:var(--marupiara-text-on-light-muted)]">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARCEIROS */}
      {partners.length > 0 && (
        <section className="py-16 bg-[color:var(--marupiara-cream)] border-y border-[color:var(--marupiara-gold)]/20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="overline text-[color:var(--marupiara-gold-dark)] mb-6 text-center">Parceiros</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              {partners.slice(0, 8).map((p) => (
                <a key={p.id} href={p.link || "#"} target="_blank" rel="noreferrer" data-testid={`home-partner-${p.id}`} className="text-center group">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} className="mx-auto h-14 object-contain grayscale group-hover:grayscale-0 transition-all" />
                  ) : (
                    <div className="font-serif text-2xl text-[color:var(--marupiara-obsidian)] group-hover:text-[color:var(--marupiara-gold-dark)] transition-colors">{p.name}</div>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      {!isMenuHidden("/contato") && (
      <section className="py-24 bg-[color:var(--marupiara-obsidian)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 50% 50%, rgba(197,160,89,0.25), transparent 65%)" }} />
        <div className="relative max-w-3xl mx-auto text-center px-6">
          <div className="overline text-[color:var(--marupiara-gold)] mb-4">Pronto para publicar?</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 text-white leading-tight">
            Seu livro merece a Marupiara.
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Solicite um orçamento sem compromisso. Nossa equipe entra em contato para desenhar o projeto editorial ideal para sua obra.
          </p>
          <Link data-testid="home-final-cta" to="/contato" className="btn-gold">
            Solicitar Orçamento <ArrowRight size={16} />
          </Link>
        </div>
      </section>
      )}
    </div>
  );
}
